<template>
  <div class="admin-dashboard">
    <!-- Top Bar -->
    <header class="admin-header">
      <div class="header-left">
        <span class="header-logo">🎭</span>
        <h1 class="header-title">Babilonia Analytics</h1>
      </div>
      <div class="header-right">
        <select v-model="selectedDays" @change="fetchData" class="period-select">
          <option :value="7">Últimos 7 días</option>
          <option :value="30">Últimos 30 días</option>
          <option :value="90">Últimos 90 días</option>
        </select>
        <button @click="fetchData" class="refresh-btn" :class="{ spinning: loading }">🔄</button>
        <button @click="logout" class="logout-btn">Salir</button>
      </div>
    </header>

    <!-- Loading -->
    <div v-if="loading && !data" class="loading-screen">
      <div class="loader"></div>
      <p>Cargando datos...</p>
    </div>

    <!-- Dashboard Content -->
    <main v-else-if="data" class="dashboard-content">
      <!-- KPI Cards -->
      <div class="kpi-grid">
        <div class="kpi-card">
          <div class="kpi-icon">👁️</div>
          <div class="kpi-info">
            <span class="kpi-value">{{ data.total_views }}</span>
            <span class="kpi-label">Visitas Totales</span>
          </div>
        </div>
        <div class="kpi-card">
          <div class="kpi-icon">👥</div>
          <div class="kpi-info">
            <span class="kpi-value">{{ data.unique_visitors }}</span>
            <span class="kpi-label">Visitantes Únicos</span>
          </div>
        </div>
        <div class="kpi-card">
          <div class="kpi-icon">📄</div>
          <div class="kpi-info">
            <span class="kpi-value">{{ data.pages.length }}</span>
            <span class="kpi-label">Páginas Visitadas</span>
          </div>
        </div>
        <div class="kpi-card">
          <div class="kpi-icon">🖱️</div>
          <div class="kpi-info">
            <span class="kpi-value">{{ totalSponsorClicks }}</span>
            <span class="kpi-label">Clicks en Sponsors</span>
          </div>
        </div>
      </div>

      <!-- Charts Row 1 -->
      <div class="charts-row">
        <!-- Daily Views Chart -->
        <div class="chart-card chart-wide">
          <h3 class="chart-title">📈 Visitas por Día</h3>
          <div class="chart-container">
            <canvas ref="dailyChart"></canvas>
          </div>
        </div>
      </div>

      <!-- Charts Row 2 -->
      <div class="charts-row charts-two-col">
        <!-- Top Pages -->
        <div class="chart-card">
          <h3 class="chart-title">📊 Páginas más Visitadas</h3>
          <div class="ranking-list">
            <div v-for="(page, i) in data.pages.slice(0, 10)" :key="i" class="ranking-item">
              <span class="ranking-pos">{{ i + 1 }}</span>
              <span class="ranking-name">{{ formatPath(page.path) }}</span>
              <div class="ranking-bar-wrap">
                <div class="ranking-bar" :style="{ width: barWidth(page.views, maxPageViews) + '%' }"></div>
              </div>
              <span class="ranking-value">{{ page.views }}</span>
            </div>
            <p v-if="data.pages.length === 0" class="no-data">Sin datos</p>
          </div>
        </div>

        <!-- Traffic Sources -->
        <div class="chart-card">
          <h3 class="chart-title">🌐 Fuentes de Tráfico</h3>
          <div class="chart-container">
            <canvas ref="sourcesChart"></canvas>
          </div>
          <div class="source-legend">
            <div v-for="(src, i) in data.traffic_sources" :key="i" class="legend-item">
              <span class="legend-dot" :style="{ background: sourceColors[i % sourceColors.length] }"></span>
              <span class="legend-label">{{ src.source }}</span>
              <span class="legend-value">{{ src.count }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Charts Row 3 -->
      <div class="charts-row charts-two-col">
        <!-- Sponsor Clicks -->
        <div class="chart-card">
          <h3 class="chart-title">🏆 Clicks por Sponsor</h3>
          <div class="ranking-list">
            <div v-for="(sponsor, i) in data.sponsor_clicks" :key="i" class="ranking-item">
              <span class="ranking-pos">{{ i + 1 }}</span>
              <span class="ranking-name">{{ sponsor.name }}</span>
              <div class="ranking-bar-wrap">
                <div class="ranking-bar ranking-bar-gold" :style="{ width: barWidth(sponsor.clicks, maxSponsorClicks) + '%' }"></div>
              </div>
              <span class="ranking-value">{{ sponsor.clicks }}</span>
            </div>
            <p v-if="data.sponsor_clicks.length === 0" class="no-data">Sin clicks aún</p>
          </div>
        </div>

        <!-- Device Distribution -->
        <div class="chart-card">
          <h3 class="chart-title">📱 Dispositivos</h3>
          <div class="chart-container">
            <canvas ref="deviceChart"></canvas>
          </div>
          <div class="source-legend">
            <div v-for="(dev, i) in (data.devices || [])" :key="i" class="legend-item">
              <span class="legend-dot" :style="{ background: deviceColors[i % deviceColors.length] }"></span>
              <span class="legend-label">{{ dev.device }}</span>
              <span class="legend-value">{{ dev.count }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Charts Row 4 -->
      <div class="charts-row charts-two-col">
        <!-- OS Distribution -->
        <div class="chart-card">
          <h3 class="chart-title">💻 Sistema Operativo</h3>
          <div class="ranking-list">
            <div v-for="(os, i) in (data.operating_systems || [])" :key="i" class="ranking-item">
              <span class="ranking-pos">{{ i + 1 }}</span>
              <span class="ranking-name">{{ os.os }}</span>
              <div class="ranking-bar-wrap">
                <div class="ranking-bar ranking-bar-blue" :style="{ width: barWidth(os.count, maxOsCount) + '%' }"></div>
              </div>
              <span class="ranking-value">{{ os.count }}</span>
            </div>
          </div>
        </div>

        <!-- Browser Distribution -->
        <div class="chart-card">
          <h3 class="chart-title">🌐 Navegador</h3>
          <div class="ranking-list">
            <div v-for="(b, i) in (data.browsers || [])" :key="i" class="ranking-item">
              <span class="ranking-pos">{{ i + 1 }}</span>
              <span class="ranking-name">{{ b.browser }}</span>
              <div class="ranking-bar-wrap">
                <div class="ranking-bar ranking-bar-green" :style="{ width: barWidth(b.count, maxBrowserCount) + '%' }"></div>
              </div>
              <span class="ranking-value">{{ b.count }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Charts Row 5 -->
      <div class="charts-row charts-two-col">
        <!-- Hourly Distribution -->
        <div class="chart-card">
          <h3 class="chart-title">🕐 Distribución por Hora</h3>
          <div class="chart-container">
            <canvas ref="hourlyChart"></canvas>
          </div>
        </div>
        <div class="chart-card empty-card"></div>
      </div>

      <!-- Last updated -->
      <p class="last-updated">Última actualización: {{ lastUpdated }}</p>
    </main>

    <!-- Error State -->
    <div v-else class="error-screen">
      <p>Error al cargar datos. <button @click="fetchData" class="retry-btn">Reintentar</button></p>
    </div>
  </div>
</template>

<script>
/* global Chart */
const API_BASE = process.env.NODE_ENV === 'production'
  ? '/api'
  : 'http://localhost:5000/api'

export default {
  name: 'AdminDashboard',
  data() {
    return {
      data: null,
      loading: false,
      selectedDays: 30,
      lastUpdated: '',
      charts: {},
      sourceColors: ['#c9a227', '#e74c3c', '#3498db', '#2ecc71', '#9b59b6', '#e67e22', '#1abc9c', '#f39c12'],
      deviceColors: ['#3498db', '#c9a227', '#2ecc71']
    }
  },
  computed: {
    totalSponsorClicks() {
      if (!this.data) return 0
      return this.data.sponsor_clicks.reduce((sum, s) => sum + s.clicks, 0)
    },
    maxPageViews() {
      if (!this.data || !this.data.pages.length) return 1
      return this.data.pages[0].views
    },
    maxSponsorClicks() {
      if (!this.data || !this.data.sponsor_clicks.length) return 1
      return this.data.sponsor_clicks[0].clicks
    },
    maxOsCount() {
      if (!this.data || !this.data.operating_systems || !this.data.operating_systems.length) return 1
      return this.data.operating_systems[0].count
    },
    maxBrowserCount() {
      if (!this.data || !this.data.browsers || !this.data.browsers.length) return 1
      return this.data.browsers[0].count
    }
  },
  mounted() {
    if (!sessionStorage.getItem('admin_token')) {
      this.$router.push('/admin')
      return
    }
    this.loadChartJS()
  },
  methods: {
    loadChartJS() {
      if (window.Chart) {
        this.fetchData()
        return
      }
      const script = document.createElement('script')
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.js'
      script.onload = () => this.fetchData()
      document.head.appendChild(script)
    },

    async fetchData() {
      this.loading = true
      const token = sessionStorage.getItem('admin_token')
      try {
        const res = await fetch(`${API_BASE}/analytics/summary?days=${this.selectedDays}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        if (res.status === 401) {
          sessionStorage.removeItem('admin_token')
          this.$router.push('/admin')
          return
        }
        this.data = await res.json()
        this.lastUpdated = new Date().toLocaleString('es-UY')
        this.$nextTick(() => this.renderCharts())
      } catch {
        this.data = null
      }
      this.loading = false
    },

    renderCharts() {
      this.renderDailyChart()
      this.renderSourcesChart()
      this.renderHourlyChart()
      this.renderDeviceChart()
    },

    renderDailyChart() {
      const ctx = this.$refs.dailyChart
      if (!ctx) return
      if (this.charts.daily) this.charts.daily.destroy()

      this.charts.daily = new Chart(ctx, {
        type: 'line',
        data: {
          labels: this.data.daily_views.map(d => {
            const date = new Date(d.date + 'T12:00:00')
            return date.toLocaleDateString('es-UY', { day: 'numeric', month: 'short' })
          }),
          datasets: [{
            label: 'Visitas',
            data: this.data.daily_views.map(d => d.views),
            borderColor: '#c9a227',
            backgroundColor: 'rgba(201, 162, 39, 0.1)',
            fill: true,
            tension: 0.4,
            pointRadius: 4,
            pointBackgroundColor: '#c9a227',
            pointBorderColor: '#000',
            pointBorderWidth: 2
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: '#111',
              titleColor: '#c9a227',
              bodyColor: '#fff',
              borderColor: '#333',
              borderWidth: 1
            }
          },
          scales: {
            x: {
              grid: { color: '#1a1a1a' },
              ticks: { color: '#666' }
            },
            y: {
              beginAtZero: true,
              grid: { color: '#1a1a1a' },
              ticks: { color: '#666', stepSize: 1 }
            }
          }
        }
      })
    },

    renderSourcesChart() {
      const ctx = this.$refs.sourcesChart
      if (!ctx || !this.data.traffic_sources.length) return
      if (this.charts.sources) this.charts.sources.destroy()

      this.charts.sources = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: this.data.traffic_sources.map(s => s.source),
          datasets: [{
            data: this.data.traffic_sources.map(s => s.count),
            backgroundColor: this.sourceColors,
            borderColor: '#111',
            borderWidth: 3
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: '#111',
              titleColor: '#c9a227',
              bodyColor: '#fff',
              borderColor: '#333',
              borderWidth: 1
            }
          },
          cutout: '65%'
        }
      })
    },

    renderHourlyChart() {
      const ctx = this.$refs.hourlyChart
      if (!ctx) return
      if (this.charts.hourly) this.charts.hourly.destroy()

      // Fill all 24 hours
      const hourlyMap = {}
      for (let i = 0; i < 24; i++) hourlyMap[i] = 0
      this.data.hourly_distribution.forEach(h => { hourlyMap[h.hour] = h.views })

      this.charts.hourly = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: Object.keys(hourlyMap).map(h => `${h}h`),
          datasets: [{
            label: 'Visitas',
            data: Object.values(hourlyMap),
            backgroundColor: 'rgba(201, 162, 39, 0.6)',
            borderColor: '#c9a227',
            borderWidth: 1,
            borderRadius: 3
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: '#111',
              titleColor: '#c9a227',
              bodyColor: '#fff'
            }
          },
          scales: {
            x: {
              grid: { display: false },
              ticks: { color: '#666', font: { size: 10 } }
            },
            y: {
              beginAtZero: true,
              grid: { color: '#1a1a1a' },
              ticks: { color: '#666', stepSize: 1 }
            }
          }
        }
      })
    },

    renderDeviceChart() {
      const ctx = this.$refs.deviceChart
      if (!ctx || !this.data.devices || !this.data.devices.length) return
      if (this.charts.device) this.charts.device.destroy()

      this.charts.device = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: this.data.devices.map(d => d.device),
          datasets: [{
            data: this.data.devices.map(d => d.count),
            backgroundColor: this.deviceColors,
            borderColor: '#111',
            borderWidth: 3
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              backgroundColor: '#111',
              titleColor: '#c9a227',
              bodyColor: '#fff',
              borderColor: '#333',
              borderWidth: 1
            }
          },
          cutout: '65%'
        }
      })
    },

    formatPath(path) {
      const names = {
        '/': 'Inicio',
        '/cartelera': 'Cartelera',
        '/galeria': 'Galería',
        '/el-grupo': 'El Grupo',
        '/obras': 'Obras',
        '/talleres/festival': 'Talleres Festival',
        '/talleres/anual': 'Taller Anual',
        '/funcion/1': 'El Quijote Popular',
        '/funcion/2': '8 Espejos',
        '/funcion/3': 'La Loca del Bequeló',
        '/funcion/4': 'Ernesto Díaz & Fabián Severo',
        '/funcion/5': 'Buceo Invisible',
        '/funcion/6': 'Noche de Circo',
        '/funcion/7': 'Tercer Fuerxa',
        '/funcion/8': 'Por culpa de la E',
        '/funcion/9': 'Naipe Marcado Trío Tango'
      }
      if (names[path]) return names[path]
      return path
    },

    barWidth(value, max) {
      return max > 0 ? Math.max((value / max) * 100, 8) : 0
    },

    logout() {
      const token = sessionStorage.getItem('admin_token')
      if (token) {
        fetch(`${API_BASE}/admin/logout`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }).catch(() => {})
      }
      sessionStorage.removeItem('admin_token')
      this.$router.push('/admin')
    }
  }
}
</script>

<style scoped>
.admin-dashboard {
  min-height: 100vh;
  background: #0a0a0a;
  color: #fff;
}

/* Header */
.admin-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 30px;
  background: #111;
  border-bottom: 1px solid #222;
  position: sticky;
  top: 0;
  z-index: 100;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-logo {
  font-size: 28px;
}

.header-title {
  font-size: 20px;
  font-weight: bold;
  color: #c9a227;
  margin: 0;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.period-select {
  background: #1a1a1a;
  color: #fff;
  border: 1px solid #333;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
}

.refresh-btn {
  background: none;
  border: 1px solid #333;
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s;
}

.refresh-btn:hover {
  border-color: #c9a227;
}

.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  100% { transform: rotate(360deg); }
}

.logout-btn {
  background: #222;
  color: #888;
  border: 1px solid #333;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s;
}

.logout-btn:hover {
  color: #e74c3c;
  border-color: #e74c3c;
}

/* Loading */
.loading-screen {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  color: #666;
}

.loader {
  width: 40px;
  height: 40px;
  border: 3px solid #222;
  border-top-color: #c9a227;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 15px;
}

/* Dashboard Content */
.dashboard-content {
  padding: 25px 30px 60px;
  max-width: 1200px;
  margin: 0 auto;
}

/* KPI Cards */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 25px;
}

.kpi-card {
  background: #111;
  border: 1px solid #222;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 15px;
  transition: border-color 0.3s;
}

.kpi-card:hover {
  border-color: #333;
}

.kpi-icon {
  font-size: 32px;
}

.kpi-info {
  display: flex;
  flex-direction: column;
}

.kpi-value {
  font-size: 32px;
  font-weight: bold;
  color: #fff;
  line-height: 1;
}

.kpi-label {
  font-size: 12px;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-top: 4px;
}

/* Charts */
.charts-row {
  margin-bottom: 20px;
}

.charts-two-col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.chart-card {
  background: #111;
  border: 1px solid #222;
  border-radius: 12px;
  padding: 25px;
}

.chart-wide {
  width: 100%;
}

.chart-title {
  font-size: 16px;
  font-weight: 600;
  color: #ccc;
  margin: 0 0 20px;
}

.chart-container {
  position: relative;
  height: 250px;
}

/* Rankings */
.ranking-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.ranking-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.ranking-pos {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #1a1a1a;
  border-radius: 50%;
  font-size: 11px;
  color: #666;
  flex-shrink: 0;
}

.ranking-name {
  font-size: 14px;
  color: #ccc;
  min-width: 100px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ranking-bar-wrap {
  flex: 1;
  height: 8px;
  background: #1a1a1a;
  border-radius: 4px;
  overflow: hidden;
}

.ranking-bar {
  height: 100%;
  background: #3498db;
  border-radius: 4px;
  transition: width 0.5s ease;
}

.ranking-bar-gold {
  background: #c9a227;
}

.ranking-bar-blue {
  background: #3498db;
}

.ranking-bar-green {
  background: #2ecc71;
}

.ranking-value {
  font-size: 14px;
  font-weight: bold;
  color: #fff;
  min-width: 30px;
  text-align: right;
}

.no-data {
  color: #444;
  font-style: italic;
  font-size: 14px;
  margin: 0;
}

.empty-card {
  border: none;
  background: transparent;
}

/* Source Legend */
.source-legend {
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.legend-label {
  font-size: 13px;
  color: #999;
  flex: 1;
}

.legend-value {
  font-size: 13px;
  font-weight: bold;
  color: #fff;
}

/* Last Updated */
.last-updated {
  text-align: center;
  font-size: 12px;
  color: #444;
  margin-top: 30px;
}

/* Error / Retry */
.error-screen {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  color: #666;
}

.retry-btn {
  background: #c9a227;
  color: #000;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: bold;
  margin-left: 10px;
}

/* Mobile */
@media (max-width: 900px) {
  .admin-header {
    flex-direction: column;
    gap: 10px;
    padding: 15px 20px;
  }

  .dashboard-content {
    padding: 20px 15px 60px;
  }

  .kpi-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  .charts-two-col {
    grid-template-columns: 1fr;
  }

  .kpi-value {
    font-size: 24px;
  }
}

@media (max-width: 480px) {
  .kpi-grid {
    grid-template-columns: 1fr;
  }

  .header-right {
    flex-wrap: wrap;
    justify-content: center;
  }
}
</style>